import React, { useState, useEffect, useContext } from "react";
import { Button, Tabs, Row, Col, Collapse } from "antd";
import { useNavigate } from "react-router-dom";
import Questions from '../questions/questions.component';
import { listFaqs } from '../../services/faq.service';
import FaqCode from "../../enums/faqCode.enum";
import "./treatments.scss";
import { RightCircleFilled } from "@ant-design/icons";
import FlowType from "../../enums/flowType.enum";
import CitationFootnote from "../citationFootnote/citationFootnote.component";
import TreatmentTimeline from "./treatmentTimeline.component";
import PosthogHelper from "../../helpers/posthog.helper";
import { UserContext } from "../../contexts/user.context";

const { Panel } = Collapse;


const citations = [
  {
    index: 1,
    authors: "R. Knopp et al.",
    title: "Effects of ezetimibe, a new cholesterol absorption inhibitor, on plasma lipids in patients with primary hypercholesterolemia.",
    journal: "European Heart Journal (2003)",
    link: "https://doi.org/10.1016/S0195-668X(02)00807-2"
  },
  {
    index: 2,
    authors: "T. Sudhop et al.",
    title: "Inhibition of Intestinal Cholesterol Absorption by Ezetimibe in Humans.",
    journal: "Circulation: Journal of the American Heart Association (2002)",
    link: "https://doi.org/10.1161/01.CIR.0000034044.95911.DC"
  },
  {
    index: 3,
    authors: "S. Hiramitsu et al.",
    title: "The effects of ezetimibe on surrogate markers of cholesterol absorption and synthesis in Japanese patients with dyslipidemia.",
    journal: "Journal of Atherosclerosis and Thrombosis (2010)",
    link: "https://doi.org/10.5551/JAT.1578"
  },
  {
    index: 4,
    authors: "A. Llewellyn et al.",
    title: "Efficacy and safety of statins, ezetimibe and statins-ezetimibe therapies for children and adolescents with heterozygous familial hypercholesterolaemia: Systematic review, pairwise and network meta-analyses of randomised controlled trials.",
    journal: "Atherosclerosis (2024)",
    link: "https://doi.org/10.1016/j.atherosclerosis.2024.118598"
  },
  {
    index: 5,
    authors: "Yong-Joon Lee et al.",
    title: "Moderate-intensity statin with ezetimibe vs. high-intensity statin in patients with diabetes and atherosclerotic cardiovascular disease in the RACING trial.",
    journal: "European Heart Journal (2022)",
    link: "https://doi.org/10.1093/eurheartj/ehac709"
  },
  {
    index: 6,
    authors: "Byeong‐Keuk Kim et al.",
    title: "Long-term efficacy and safety of moderate-intensity statin with ezetimibe combination therapy versus high-intensity statin monotherapy in patients with atherosclerotic cardiovascular disease (RACING): a randomised, open-label, non-inferiority trial.",
    journal: "The Lancet (2022)",
    link: "https://doi.org/10.1016/S0140-6736(22)00916-3"
  },
  {
    index: 7,
    authors: "T. Pearson et al.",
    title: "A community-based, randomized trial of ezetimibe added to statin therapy to attain NCEP ATP III goals for LDL cholesterol in hypercholesterolemic patients: the ezetimibe add-on to statin for effectiveness (EASE) trial.",
    journal: "Mayo Clinic proceedings (2005)",
    link: "https://doi.org/10.4065/80.5.587"
  },
  {
    index: 8,
    authors: "R. Knopp et al.",
    title: "Effects of ezetimibe, a new cholesterol absorption inhibitor, on plasma lipids in patients with primary hypercholesterolemia.",
    journal: "European Heart Journal (2003)",
    link: "https://doi.org/10.1016/S0195-668X(02)00807-2"
  },
  {
    index: 9,
    authors: "R. Giugliano et al.",
    title: "Benefit of Adding Ezetimibe to Statin Therapy on Cardiovascular Outcomes and Safety in Patients With vs. Without Diabetes: Results from IMPROVE-IT.",
    journal: "Circulation (2017)",
    link: "https://doi.org/10.1161/CIRCULATIONAHA.117.030950"
  },
  {
    index: 10,
    authors: "Y. Ouchi et al.",
    title: "Ezetimibe Lipid-Lowering Trial on Prevention of Atherosclerotic Cardiovascular Disease in 75 or Older (EWTOPIA 75): A Randomized Controlled Trial.",
    journal: "Circulation (2019)",
    link: "https://doi.org/10.1161/CIRCULATIONAHA.118.039415"
  },
  {
    index: 11,
    authors: "R. Giugliano et al.",
    title: "Benefit of Adding Ezetimibe to Statin Therapy on Cardiovascular Outcomes and Safety in Patients With Versus Without Diabetes Mellitus: Results From IMPROVE-IT (Improved Reduction of Outcomes Vytorin Efficacy International Trial).",
    journal: "Circulation (2018)",
    link: "https://doi.org/10.1161/CIRCULATIONAHA.117.030950"
  },

  {
    index: 12,
    authors: "R. Bach et al.",
    title: "Effect of Simvastatin-Ezetimibe Compared With Simvastatin Monotherapy After Acute Coronary Syndrome Among Patients 75 Years or Older: A Secondary Analysis of a Randomized Clinical Trial.",
    journal: "JAMA cardiology (2019)",
    link: "https://doi.org/10.1001/jamacardio.2019.2306"
  },
  {
    index: 13,
    authors: "E. Bohula et al.",
    title: "Prevention of Stroke with the Addition of Ezetimibe to Statin Therapy in Patients With Acute Coronary Syndrome in IMPROVE-IT (Improved Reduction of Outcomes: Vytorin Efficacy International Trial).",
    journal: "Circulation (2017)",
    link: "https://doi.org/10.1161/CIRCULATIONAHA.117.029095"
  },
  {
    index: 14,
    authors: "S. Murphy et al.",
    title: "Reduction in Total Cardiovascular Events With Ezetimibe/Simvastatin Post-Acute Coronary Syndrome: The IMPROVE-IT Trial.",
    journal: "Journal of the American College of Cardiology (2016)",
    link: "https://doi.org/10.1016/j.jacc.2015.10.077"
  }

  

];

const mechanismData = [
  {
    key: "1",
    label: "🛑 Blocks Cholesterol Absorption",
    content: (
      <>
        <p>
          <strong className="pathway-lead-in">Ezetimibe works by blocking cholesterol at the gate: your gut.</strong>
        </p>

        <p>Most cholesterol in your bloodstream doesn’t come from food directly. It comes from a recycling loop between your gut and liver. The liver secretes cholesterol into the intestine through bile, and a specialized protein called Niemann-Pick C1-Like 1 (NPC1L1) helps reabsorb it back into the body.</p>

        <p>Ezetimibe targets that protein. By binding to NPC1L1 in the small intestine, ezetimibe blocks the reabsorption of cholesterol and keeps it from entering your bloodstream. The result: less circulating cholesterol, fewer ApoB-containing particles, and lower LDL cholesterol levels.</p>

        <p>This mechanism is completely different from statins, which work in the liver. Ezetimibe works in the intestine and complements liver-based therapies by attacking the problem from another angle.</p>
  
      </>
    ),
  },
  {
    key: "2",
    label: "🔄 Clears Cholesterol from the Bloodstream",
    content: (
      <>
        <p><strong className="pathway-lead-in">By reducing cholesterol absorption, ezetimibe forces the body to pull more from the blood.</strong></p>
        <p>
        When your gut absorbs less cholesterol, your liver senses a shortfall and responds by pulling more cholesterol out of the bloodstream. It does this by increasing the number of LDL receptors which are proteins that capture LDL and other ApoB-containing particles and remove them from circulation.
        </p>
        <p>
        This means ezetimibe not only reduces how much cholesterol enters the body, but also boosts the body’s ability to clear cholesterol already present. When combined with a statin, these effects stack: statins reduce cholesterol production, and ezetimibe reduces absorption, both feeding the same clearance mechanism for greater total ApoB and LDL lowering.
        </p>
 
      </>
    ),
  },
  {
    key: "3",
    label: "🎯 Leaves Muscles & Liver Largely Untouched",
    content: (
      <>
        <p><strong className="pathway-lead-in">Because ezetimibe acts locally, side effects are very rare.</strong></p>
        <p>
        Unlike statins, which are extensively metabolized by the liver and can affect muscle tissue, ezetimibe undergoes minimal liver processing and has virtually no impact on muscle enzymes. It targets the intestine to block cholesterol absorption, without disrupting hormone production, energy metabolism, or muscle function.
        </p>
        <p>
        This makes it a preferred option for people who cannot tolerate statins or who need additional cholesterol lowering with minimal added risk. Its excellent safety profile has been confirmed in large clinical trials involving a wide range of individuals, including older adults, people with diabetes, and patients with liver disease.
        </p>
   
      </>
          
    ),
  },

];

const timelineItems = [
  {
    key: '1',
    label: 'Upload Lab Work',
    children: {
      label: 'STEP 1',
      title: 'Upload Lab Work',
      description: <>
        Confirm your eligibility for ezetimibe treatment with recent lab results and short health questionnaire.
      </>,
      img: '/img/timeline-baseline.jpg',
      body: <>
        <ul className="timeline-list">
          <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Required: <a href="/test/apob" target="_blank">ApoB</a> or <a href="/test/direct-ldl" target="_blank">LDL cholesterol</a> within past 12 months
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                Quick and easy health questionnaire to ensure safe prescribing.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Need labs? Get Instalab's <a href="/heart-health-test" target="_blank">Advanced Heart Health Test</a> for a complete picture.
                </Col>
              </Row>
            </li>
          </ul>
        </>
      }
  },
  {
    key: '2',
    label: `Start Treatment`,
    children: {
      label: 'STEP 2',
      title: 'Start Treatment',
      description: <>
        A board-certified lipidologist will personally review your lab results and health info to ensure ezetimibe is the right fit for you.
      </>,
      img: '/img/treatments/medication-delivery.png',
      body: <>
  
          <ul className="timeline-list">
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Reviewed and approved by a lipid specialist.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                Medication shipped directly to your door.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Concierge support available for any follow-up questions.
                </Col>
              </Row>
            </li>
          </ul>
        </>
      }
  }, 
  {
    key: '3',
    label: 'Track Progress',
    children: {
      label: 'STEP 3',
      title: 'Track Progress',
      description: <>
        See how your body is responding with a follow-up lab panel at 6–12 weeks.
      </>,
      img: '/img/timeline-chart.jpg',
      body: <>
          <ul className="timeline-list">
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Look for a drop in ApoB and LDL cholesterol.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  If taking with statin, monitor liver (ALT) and blood sugar (HbA1c) for increases.
                </Col>
              </Row>
            </li>
            <li>
              <Row align="top">
                <Col>
                  <RightCircleFilled className="timeline-icon" />
                </Col>
                <Col>
                  Note any changes in energy, mood, or muscle comfort.
                </Col>
              </Row>
            </li>
          </ul>
          <p>Not seeing expected results? You can adjust your medication or consult a physician through our <a href="/heart">Heart Health Program</a>.</p>
        </>
      }
  }

]

const safetyInfo = [
    {
      key: "1",
      title: "Safe for Most at Standard Dose",
      content: (<>
        <p>
        Ezetimibe 10 mg is well tolerated by most people and has a lower side effect burden than many other cholesterol-lowering medications. It can be used alone or added to statins when more cholesterol reduction is needed.
        </p>

        <p>This medication does not require doctor visits to maintain your prescription. However, we recommend:
          <ul>
            <li>Monitor your lab results regularly, including LDL Cholesterol and ApoB, to track your progress</li>
            <li>Check in with your healthcare provider if you have questions, health changes, or are unsure about your results</li>
            <li>Report any side effects or concerns early, especially if your cholesterol levels aren’t improving as expected</li>
          </ul>
        </p>

        <p>If you’re taking ezetimibe as an add-on to a statin or other therapy, or have complex health needs, our <a href="/heart">Heart Health Program</a> offers additional physician oversight and safety tracking.</p>
      </>)
    },  
    {
      key: "2",
      title: "Possible Side Effects",
      content: (<>
        <p><strong>Possible and Usually Mild</strong></p>
      <p>Most people tolerate ezetimibe well. If symptoms occur, they are often mild and short-lived. These may include:
        <ul>
            <li>Headache or fatigue</li>
            <li>Nasal congestion, sore throat</li>
            <li>Mild joint or back pain</li>
            <li>Digestive changes (e.g., diarrhea)</li>
        </ul>
      </p>

      <p><strong>Very Rare but Serious</strong></p>

      <p>Although very rare, more serious effects can occur, especially when ezetimibe is used in combination with statins or in people with certain health conditions. These include:
      <ul>
        <li>Liver injury: Can occur alone or more often with statins. Look for yellowing of skin/eyes, abdominal pain, or persistent nausea.</li>
        <li>Muscle-related issues: Myopathy or rhabdomyolysis is rare but possible, especially when combined with statins or cyclosporine. Watch for muscle pain, dark urine, or profound weakness.</li>
        <li>Autoimmune hepatitis: Rare cases have been reported; corticosteroid treatment may be needed.</li>
        <li>Thrombocytopenia (low platelets): Extremely rare; can present with unusual bruising or bleeding.</li>
      </ul>
      </p>

      <p>If any of these occur, stop the medication immediately and seek medical attention.</p>
      </>)
    },
    {
      key: "4",
      title: "Liver & Muscle Monitoring",
      content: (<>
        <p>Ezetimibe is not broken down by CYP enzymes, which are the liver’s main system for processing many medications, including most statins. This means ezetimibe has fewer drug interactions than some other cholesterol-lowering medications. However, it is still processed by the liver and eliminated in bile, so liver monitoring is important, especially when used alongside other lipid-lowering drugs.
        </p>
        <p>To ensure safe use, we recommend the following:
        <ul>
          <li>Baseline liver function testing before starting (ALT, AST).</li>
          <li>Periodic rechecks, especially if you’re combining ezetimibe with a statin or have known liver conditions.</li>
          <li>Promptly report symptoms such as fatigue, muscle pain, yellowing of the skin or eyes (jaundice), or dark urine.</li>
        </ul>
        </p>
        <p>People with pre-existing liver disease, heavy alcohol use, or kidney impairment may need closer monitoring, as both liver and kidney function can influence how the drug is processed and tolerated.</p>
        <p>Although ezetimibe on its own is less likely to cause muscle issues than statins, cases of myopathy and rhabdomyolysis have occurred—usually when combined with statins or cyclosporine. If you notice unusual muscle soreness, weakness, or urine that appears dark or tea-colored, stop the medication and seek medical attention.</p>
      </>)
    },
    {
      key: "5",
      title: "Who Should Be Cautious",
      content: (<>

        <p>Ezetimibe is generally safe, but talk to your provider before starting if you:</p>
        <ul>
          <li>Have severe liver disease</li>
          <li>Have severe kidney disease</li>
          <li>Are over 70 and also taking a statin</li>
          <li>Take cyclosporine or fibrates</li>
          <li>Are pregnant, breastfeeding, or planning pregnancy</li>
        </ul>

        <p>If any of these apply, ezetimibe may not be appropriate without closer physician oversight. Consider joining our <a href="/heart">Heart Health Program</a> to receive tailored guidance and safe long-term care.</p>
      </>)
    },
    {
      key: "6",
      title: "Medication & Supplement Interactions",
      content: (<>  
        <p>Ezetimibe can interact with other medications, especially those affecting lipid metabolism. Important interactions include:</p>
        <ul>
          <li>Cyclosporine: Can increase ezetimibe levels up to 12-fold. Requires monitoring.</li>
          <li>Fibrates (especially fenofibrate): May increase gallstone risk. Monitor for gallbladder symptoms.</li>
          <li>Statins: When combined, there’s a slight increased risk of liver or muscle toxicity.</li>
          <li>Bile acid sequestrants (e.g., cholestyramine): Reduce ezetimibe absorption. Space doses appropriately (ezetimibe 2 hours before or 4 hours after).</li>
          <li>Warfarin: Possible INR changes reported. Monitor closely if used together.</li>
        </ul>
        <p>Please disclose all medications and supplements during your intake process so we can safely evaluate your prescription.</p>
      </>)
    },
    {
      key: "7",
      title: "Pregnancy & Breastfeeding",
      content: (<>
        <p>Ezetimibe is not recommended during pregnancy, as cholesterol is essential for fetal development.</p>

        <p>For breastfeeding:</p>
        <ul>
          <li>Small amounts of ezetimibe and its metabolites appear in breast milk.</li>
          <li>Monotherapy may be acceptable during lactation.</li>
          <li>Avoid use with statins while nursing.</li>
        </ul>
        <p>Always consult your provider if pregnant, planning pregnancy, or nursing.</p>
      </>)
    },
    {
      key: "8",
      title: "When to Stop and Seek Medical Advice",
      content: (<>
        <p>Discontinue ezetimibe and seek medical care if you experience:</p>
        <ul>
          <li>Unexplained muscle pain or weakness</li>
          <li>Yellowing of the eyes or skin (jaundice)</li>
          <li>Dark-colored urine or clay-colored stools</li>
          <li>Facial swelling, rash, or difficulty breathing</li>
          <li>Bruising or bleeding not caused by injury</li>
        </ul>
      </>)
    },
    {
      key: "9",
      title: "About the Heart Health Program",
      content: (<>
        <p>Our <a href="/heart">Heart Health Program</a> is our comprehensive, physician-led care model, which is designed for people who want more support, more data, and more personalized insight.</p>
      
        <p>This program is ideal if you:
        <ul>
          <li>Combine ezetimibe with a statin or other drugs</li>
          <li>Have underlying liver, kidney, or metabolic concerns</li>
          <li>Want help interpreting labs and optimizing treatment</li>
          <li>Have experienced side effects from other cholesterol treatments</li>
        </ul>
        </p>
        <p>What’s Included:
        <ul>
          <li>Comprehensive lab testing, including LDL-C, ApoB, glucose, liver function, and more</li>
          <li>1-on-1 physician consultations for treatment planning, lab interpretation, and progress tracking</li>
          <li>Personalized plan to lower ApoB and improve long-term heart health</li>
          <li>Ongoing monitoring to help optimize your regimen and manage side effects</li>
        </ul>
        </p>
        <p>Whether you’re just getting started or managing multiple health priorities, the Heart Health Program gives you the medical partnership you need to move forward with confidence.
        </p>
        <p><Button type="primary" size="large" onClick={() => window.location.href = '/heart'}>Learn More</Button></p>
        </>)
    }
]

export const Ezetimibe = () => {
  const [faqs, setFaqs] = useState([]);
  const [tabPosition, setTabPosition] = useState("left");
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext)

  useEffect(() => {
    const fetchFaqs = async () => {
      const filter = {
        code: { 
          $in: [
            FaqCode.EZETIMIBE_WHY_TAKE,
            FaqCode.EZETIMIBE_DIFFERENCE_FROM_STATIN,
            FaqCode.EZETIMIBE_COMBINED_WITH_STATIN,
            FaqCode.EZETIMIBE_HOW_TO_KNOW,
            FaqCode.EZETIMIBE_STRONG_ENOUGH,
            FaqCode.EZETIMIBE_EVERY_OTHER_DAY,
            FaqCode.EZETIMIBE_PHYSICIAN,    
            FaqCode.EZETIMIBE_INSURANCE,
          ]   
        }
      }
      const response = await listFaqs({filter})
      setFaqs(response)
    }

    fetchFaqs()
  }, [])

  useEffect(() => {
    document.title = "Ezetimibe (10 mg) - Push ApoB and LDL Cholesterol Even Lower"
  }, [])

  useEffect(() => {
    PosthogHelper.track(currentUser, `[treatment] ezetimimbe view landing`, true)
  }, [currentUser])

  useEffect(() => {
    function handleResize() {
      setTabPosition(window.innerWidth < 768 ? "top" : "left");
    }
    handleResize(); // set on mount
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <div className="treatment-detail">
      {/* Hero Section */}
      <section className="treatment-hero">
        {/* Only for desktop */}
        <div className="treatment-hero__image treatment-hero__image--desktop">
          <img src="/img/treatments/ezetimibe-on-table.webp" alt="Ezetimibe Bottle" />
        </div>
        <div className="treatment-hero__info">
          <h1>Ezetimibe (10 mg)<span className="treatment-hero__caption">: Push ApoB and LDL Cholesterol Even Lower</span></h1>
          <div className="treatment-hero__badges">
              <span className="badge">Clinically Backed</span>
              <span className="badge">HSA/FSA Eligible</span>
              <span className="badge">Free Shipping</span>
          </div>

          {/* Only for mobile */}
          <div className="treatment-hero__image treatment-hero__image--mobile">
            <img src="/img/treatments/ezetimibe-bottle.webp" alt="Ezetimibe Bottle" />
          </div>
          <p className="treatment-hero__subtitle">
              Ezetimibe blocks cholesterol absorption in the gut, helping reduce ApoB and LDL cholesterol beyond what diet or statins alone can achieve.
          </p>

          <div className="treatment-hero__price">
            <span>Starting at $15/month</span>
          </div>

          <div className="treatment-hero__features">
    
            <div className="treatment-hero__feature">
              <h4>Well Tolerated with Reliable Results</h4>
              <p>Ezetimibe can lower ApoB and LDL by 17-21% on its own and boost statin effects by an additional 15-25%, with one of the best tolerability profiles available.
            
              </p>
            </div>
            <div className="treatment-hero__feature">
              <h4>Same Day Prescription Approval</h4>
              <p>Your prescription is evaluated and approved by a board-certified lipidologist same day. This ensures this medication is safe for you.</p>
            </div>
            <div className="treatment-hero__feature">
              <h4>Trusted Pharmacy Partners</h4>
              <p>Your medication is filled through licensed U.S. pharmacies that follow strict protocols for quality, consistency, and safety, so every tablet meets the highest standard.</p>
            </div>
          </div>
          <div className="treatment-hero__cta">
            

            <div className="cta-container">
           
            <Button className="cta" type="primary" size="large" onClick={() => navigate(`/flow/${FlowType.EZETIMIBE}`)}>Get Started Now</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="treatment-benefits">
        <div className="max-width-container">
        <h2>The Benefits of Ezetimibe
          <div className="caption">A unique, low-risk treatment that supports long-term cardiovascular prevention.</div>
        </h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <h3>Lowers ApoB and LDL Cholesterol</h3>
            <p>
             Ezetimibe reduces ApoB and LDL cholesterol by 17-21% as a standalone treatment, making it a reliable option for people who need modest lowering or cannot tolerate statins.
              <CitationFootnote number={1} citation={citations[0]} />
              <CitationFootnote number={2} citation={citations[1]} />
              <CitationFootnote number={3} citation={citations[2]} />.
            </p>
          </div>

          <div className="benefit-card">
            <h3>Boosts Statin Effectiveness</h3>
            <p>When used alongside a statin like rosuvastatin, ezetimibe adds an extra 15–25% reduction in ApoB and LDL cholesterol on top of what statins alone provide without increasing the risk of side effects
              <CitationFootnote number={4} citation={citations[3]} />
              <CitationFootnote number={5} citation={citations[4]} />
              <CitationFootnote number={6} citation={citations[5]} />
              <CitationFootnote number={7} citation={citations[6]} />.
            </p>
          </div>
          <div className="benefit-card">
            <h3>Excellent Tolerability and Safety</h3>
            <p>
            Unlike statins, it doesn’t affect muscle tissue and isn’t processed by the liver’s main drug-metabolizing enzymes. Instead, it works in the gut and is metabolized through a milder pathway, making side effects like muscle pain or liver issues very rare.        
              <CitationFootnote number={8} citation={citations[7]} />
              <CitationFootnote number={9} citation={citations[8]} />
              <CitationFootnote number={10} citation={citations[9]} />.
            </p>
          </div>
          <div className="benefit-card">
            <h3>Helps Prevent Heart Attack and Stroke</h3>
            <p>Ezetimbe, when added to a statin, further lowers the risk of cardiovascular events like heart attack and stroke by about 13–24% and 17–21% respectively
                <CitationFootnote number={9} citation={citations[8]} />
                <CitationFootnote number={11} citation={citations[10]} />
                <CitationFootnote number={12} citation={citations[11]} />
                <CitationFootnote number={13} citation={citations[12]} />
                <CitationFootnote number={14} citation={citations[13]} />.
              </p>
          </div>
 
        </div>
        </div>
      </section>

      {/* Treatment Steps */}
      <section className="treatment-steps">
        <div className="max-width-container">
          <h2>How to Start Ezetimibe
            <div className="caption">A simple, safe, and data-driven way to begin treatment.</div>
          </h2>
          <TreatmentTimeline timelineItems={timelineItems} />
        </div>
      </section>

      {/* Mechanism Section */}
      <section className="treatment-pathways">
        <div className="max-width-container">
        <h2>How Ezetimibe Works
          <div className="caption">The pharmacology behind its powerful, targeted effects.</div>
        </h2>
        <div className="pathways-tabs">
        <Tabs
          defaultActiveKey="1"
          tabPosition={tabPosition}
          items={mechanismData.map(tab => ({
            key: tab.key,
            label: tab.label,
            children: tab.content,
          }))}

        />
        </div>
      </div>

      </section>

      {/* Safety Information */}
      <section className="treatment-safety">
      <div className="max-width-container">
        <h2>Safety Information
        </h2>

        <div className="safety-info-intro">
          <p>Ezetimibe is a cholesterol absorption inhibitor used to lower LDL cholesterol and ApoB by blocking dietary and biliary cholesterol uptake in the small intestine. Unlike statins, ezetimibe doesn’t interfere with cholesterol production in the liver, making it a well-tolerated option for people seeking gentler alternatives or complements to statins.
          </p>
        
          <p>To ensure appropriate use, we require recent lab results showing your LDL cholesterol and ApoB levels before starting treatment. If you haven’t had these done in the past year, we can help you get them done.</p>
        
          <p>If you're looking for structured medical oversight, we offer the <a href="/heart">Heart Health Program</a>, which is a physician-led option that includes comprehensive lab testing, 1-on-1 medical support, and a personalized approach to managing both heart and metabolic health.
          </p>
        </div>

        <Collapse
          accordion
          bordered={false}
          className="safety-collapse"
        >
          {safetyInfo.map(item => (
            <Panel header={item.title} key={item.key}>
              {item.content}
            </Panel>
          ))}
     
        </Collapse>
      </div>
      </section>


      {/* FAQ */}
      {faqs?.length > 0 && 
      <section className="treatment-faq">
        <div className="max-width-container">
          <h2>Frequently Asked Questions</h2>
          <Questions items={faqs} />
        </div>
      </section>}


      {/* Related Articles */}
      {/* https://instalab.com/research/statin */}
      {/* https://instalab.com/research/evaluate-medication */}
      {/* https://instalab.com/research/sterol-test */}

    </div>
  );
}

export default Ezetimibe;